import { css, nothing } from 'lit'
import { html } from 'lit/static-html.js'
import HHQuizElement from '../element.js'
import { repeat } from 'lit/directives/repeat.js'
import { addToCart } from '../../lib/utils/requests.js'
import { reRenderShopifySections } from '../../lib/utils/shopify.js'
import { formatPriceIntl } from '../../lib/utils/general.js'

/**
 * Selected products list to be added to order
 * @extends
 */

class HHQuizSelectedProductsOrder extends HHQuizElement {
  static properties = {
    _addingToCart: { state: true },
  }

  constructor() {
    super()
    this._addingToCart = false
  }

  get subtotal() {
    return this.store.hydratedOrderLines.reduce((total, line) => {
      return total + line.variant.price * line.quantity
    }, 0)
  }

  get cartSubtotal() {
    return this.subtotal + this.store.cart.items_subtotal_price
  }

  async addAllToCart() {
    this._addingToCart = true
    let productId = null
    const formData = {
      items: []
    }

    this.store.hydratedOrderLines.forEach(lineItem => {
      // this.$lineItems.forEach(lineItem => {
      formData.items.push({
        id: Number(lineItem.variant.id),
        quantity: Number(lineItem.quantity)
      })
      productId = Number(lineItem.variant.product.id)
    })

    if (formData.items.length > 0) {
      reRenderShopifySections(await addToCart(formData))

      const notificationId = `cart-notification-product-${productId}`;
      this.dispatchEvent(
        new CustomEvent('productAdded', {
          bubbles: true,
          composed: true,
          detail: {
            id: notificationId,
            productId: productId,
            multipleProducts: this.store.hydratedOrderLines.length,
          },
        })
      )
    }

    this.store.clearOrderLines()
    this.store.killLocalStorage()
    this._addingToCart = false
  }


  get orderLimits(){
    return this.store.hydratedOrderLines.map(({ variant, quantity }) => {
      const cartQuantity = this.store.cart.items.filter(i => i.variant_id === variant.id).reduce((acc, i) => acc + i.quantity, 0)
      return {
        limit: variant.order_limit,
        quantity,
        cartQuantity,
        availableQuantity: Math.max(0, variant.order_limit - cartQuantity),
        willExceedLimit: Boolean(variant.order_limit && variant.order_limit < (cartQuantity + quantity)),
        hasReachedLimit: Boolean(variant.order_limit && variant.order_limit <= (cartQuantity + quantity))
      }
    })
  }

  get limitsExceeded() {
    return this.orderLimits.find(i => i.willExceedLimit)
  }

  get checkoutDisabled() {
    return !this.store.hydratedOrderLines.length ||
    this._addingToCart || this.limitsExceeded
  }

  /**
   * Renders list of selected products to be added to order
   * @returns {TemplateResult}
   */
  render() {
    const orderLimits = this.orderLimits
    return html`
      <div class="hh-quiz-order__section hh-quiz-order__lines">
        ${!this.store.hydratedOrderLines.length
          ? html` <p class="body"> ${HH_QUIZ.t.cart.no_items}</p> `
          : repeat(
              this.store.hydratedOrderLines,
              (line) => line.id,
            ({ variant, quantity, id }, index) => html`<hh-quiz-product-card
              .variant="${variant}"
              .quantity="${quantity}"
              .id="${id}"
              .max="${orderLimits[index].availableQuantity}"
              ><div slot="footer" class="caption ${orderLimits[index].willExceedLimit ? 'hh-quiz-order__limit-error' : 'hh-quiz-order__limit-warn'}">
              ${orderLimits[index].hasReachedLimit ? html`
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="14" height="14" rx="7" fill="currentColor"/>
                  <path d="M7 2V8" stroke="#FAF9F8" stroke-width="2"/>
                  <circle cx="7" cy="10.5" r="0.5" fill="#FAF9F8" stroke="#FAF9F8"/>
                </svg>
                ${HH_QUIZ.t.cart.order_limit.replaceAll('{{ limit }}', orderLimits[index].limit).replaceAll('{{ cart_quantity }}', orderLimits[index].cartQuantity).replaceAll('{{ product_title }}', variant.product.title)}.
                ${orderLimits[index].cartQuantity > 0 ? HH_QUIZ.t.cart.order_limit_in_cart_message.replaceAll('{{ limit }}', orderLimits[index].limit).replaceAll('{{ cart_quantity }}', orderLimits[index].cartQuantity).replaceAll('{{ product_title }}', variant.product.title) : nothing}
              ` : nothing}
              </div></hh-quiz-product-card>
              `
            )}
      </div>
      <div class="hh-quiz-order__section hh-quiz-order__summary">
        ${this.store.quiz.freeShippingFrom ? html`
          <div class="body hh-quiz-order__shipping" tabindex="0">
            <p class="body">${this.cartSubtotal < this.store.quiz.freeShippingFrom ? this.store.quiz.texts.results.freeShippingFrom : HH_QUIZ.t.cart.free_shipping_accepted}</p>
            <hh-quiz-progress-bar rounded .progress="${1/this.store.quiz.freeShippingFrom * this.cartSubtotal}" animate></hh-quiz-progress-bar>
          </div>
        ` : nothing}
        <div class="body hh-quiz-order__subtotal">
          <div> ${HH_QUIZ.t.cart.subtotal}</div>
          <div tabindex="0" title="${HH_QUIZ.t.cart.subtotal}">${formatPriceIntl(this.subtotal)}</div>
        </div>
        <hh-button
          @keydown="${(ev) => ev.stopPropagation()}"
          @click="${() => this.addAllToCart()}"
          width="full"
          .disabled="${this.checkoutDisabled}"
        >
          ${this._addingToCart ? HH_QUIZ.t.cart.adding : HH_QUIZ.t.cart.add_to_cart}
        </hh-button>
        ${this.limitsExceeded ? html`
            <div class="hh-quiz-order__limit-error__submit caption">
              ${HH_QUIZ.t.cart.order_limit_submit_message}
            </div>
        ` : nothing}
      </div>
    `
  }

  static styles = [
    HHQuizElement.styles,
    css`
      .hh-quiz-order__section {
        padding: 40px 60px;
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .hh-quiz-order__section {
          padding: 40px 20px;
        }
      }

      .hh-quiz-order__section:not(:last-child) {
        border-bottom: 1px solid var(--hh-color-grey-mineral);
      }

      .hh-quiz-order__section.hh-quiz-order__summary {
        padding-top: 20px;
      }

      .hh-quiz-order__lines {
        display: grid;
        gap: 20px;
      }
      .hh-quiz-order__line {
        padding: 0 0;
      }
      .hh-quiz-order__line__actions {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin-top: 10px;
      }

      button[name="minus"] svg,
      button[name="plus"] svg {
        width: 10px;
        height: auto;
      }

      .hh-quiz-order__line * {
        color: var(--hh-color-aged-black);
      } 

      .hh-quiz-order__line .caption {
        color: var(--hh-color-ancient-ore);
      }

      .hh-quiz-order__subtotal {
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px;
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .hh-quiz-order__subtotal {
          margin-bottom: 10px;
        }
      }

      .hh-quiz-order__shipping {
        padding: 0 20px;
        margin-bottom: 20px;
        text-align: center;
      }

      .hh-quiz-order__shipping > p {
        margin-bottom: 10px;
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .hh-quiz-order__shipping {
          padding: 0;
          margin-bottom: 10px;
        }
      }

      .hh-quiz-order__limit-error,
      .hh-quiz-order__limit-warn {
        padding-left: calc(max(90px, 6vw) + 20px);
        display: flex;
        align-items: start;
      }

      .hh-quiz-order__limit-error__submit {
        text-align: center;
      }

      .hh-quiz-order__limit-error,
      .hh-quiz-order__limit-warn,
      .hh-quiz-order__limit-error__submit {
        color: #EC0001;
        line-height: 1.3em;
        margin-top: 10px;
      }

      .hh-quiz-order__limit-error svg,
      .hh-quiz-order__limit-warn svg {
        margin-right: 5px;
        margin-top: 1px
      }
    `,
  ]
}

customElements.define(
  'hh-quiz-selected-products-order',
  HHQuizSelectedProductsOrder
)
